/**
 * Add Gemini Onboarding Contact modal (OR-1326).
 *
 * Mirrors the AgentModal interaction: the Email field is an open text
 * input with live pool suggestions (`/v2/contacts/?search=`). Picking
 * a suggestion locks the row to that existing contact and autofills
 * Name / Job Title / Phone — editing the name afterwards unlocks and
 * forks a brand-new contact instead of mutating the shared pool row
 * (contacts are global; a PATCH would leak into other submissions).
 *
 * Save behaviour:
 *   - Existing contact picked (and left untouched) → hand it back as-is;
 *     the caller attaches it. No duplicate is created.
 *   - Otherwise → POST /v2/contacts/ with the typed fields, hand back
 *     the created contact.
 *
 * Field rules (design, OR-1310): Email, Name and Job Title required —
 * phone optional. Job Title offers the four canonical codes plus free
 * text (the backend accepts custom titles).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Check, Mail01, Phone, X } from "@untitledui/icons";
import { z } from "zod";
import { Button, ButtonUtility, Dialog, Input, Modal, ModalOverlay } from "@tricura/atlas-ui";
import { createPoolContact, searchContactPool } from "../api";
import type { OnboardingContact } from "../types/onboarding-contact";
import { JOB_TITLE_PRESETS, displayJobTitle, jobTitleToWire } from "../types/onboarding-contact";
import { SelectWithCustom } from "./select-with-custom";

/** Same paste-friendly phone filter the Facility Contacts fields use. */
const sanitizePhone = (value: string) => value.replace(/[^\d\s+()-]/g, "");

const schema = z.object({
    /** Set only when the user picked a pool suggestion. Empty = new. */
    contactId: z.string(),
    email: z.string(),
    name: z.string(),
    jobTitle: z.string(),
    phone: z.string(),
});

type FormData = z.infer<typeof schema>;

// Error copy comes straight from the design's validation states.
const refinedSchema = schema.superRefine((data, ctx) => {
    const email = data.email.trim();
    if (email.length === 0) {
        ctx.addIssue({ code: "custom", path: ["email"], message: "Select or add email" });
    } else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
        ctx.addIssue({ code: "custom", path: ["email"], message: "Invalid email format. Please check the format and try again." });
    }
    if (data.name.trim().length === 0) {
        ctx.addIssue({ code: "custom", path: ["name"], message: "Add name" });
    }
    if (data.jobTitle.trim().length === 0) {
        ctx.addIssue({ code: "custom", path: ["jobTitle"], message: "Select Job title" });
    }
});

interface OnboardingContactModalProps {
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
    /** Fires with the contact to attach — existing (picked) or newly created. */
    onDone: (contact: OnboardingContact) => void | Promise<void>;
}

export function OnboardingContactModal({ isOpen, onOpenChange, onDone }: OnboardingContactModalProps) {
    return (
        <ModalOverlay isOpen={isOpen} onOpenChange={onOpenChange}>
            <Modal>
                <Dialog>
                    {({ close }) => (
                        <OnboardingContactModalBody
                            close={close}
                            onDone={async (contact) => {
                                await onDone(contact);
                                close();
                            }}
                        />
                    )}
                </Dialog>
            </Modal>
        </ModalOverlay>
    );
}

interface BodyProps {
    close: () => void;
    onDone: (contact: OnboardingContact) => void | Promise<void>;
}

function OnboardingContactModalBody({ close, onDone }: BodyProps) {
    const { control, handleSubmit, watch, setValue, getValues } = useForm<FormData>({
        resolver: zodResolver(refinedSchema),
        mode: "onTouched",
        defaultValues: { contactId: "", email: "", name: "", jobTitle: "", phone: "" },
    });

    const [submitting, setSubmitting] = useState(false);
    const [serverError, setServerError] = useState<string | null>(null);
    const [suggestions, setSuggestions] = useState<OnboardingContact[]>([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);

    const contactId = watch("contactId");
    const email = watch("email");
    const isLocked = !!contactId;

    // Debounced pool search while the email is being typed (same pattern
    // as the AgentModal brokerage lookup).
    const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    useEffect(() => {
        if (isLocked) {
            setSuggestions([]);
            return;
        }
        const q = email.trim();
        if (q.length < 1) {
            setSuggestions([]);
            return;
        }
        if (searchTimer.current) clearTimeout(searchTimer.current);
        searchTimer.current = setTimeout(() => {
            searchContactPool(q, 8)
                .then((rows) => {
                    if (getValues("email").trim() !== q) return;
                    setSuggestions(rows);
                    setActiveIndex(-1);
                })
                .catch(() => setSuggestions([]));
        }, 200);
        return () => {
            if (searchTimer.current) clearTimeout(searchTimer.current);
        };
    }, [email, isLocked, getValues]);

    const selectSuggestion = useCallback(
        (c: OnboardingContact) => {
            setValue("contactId", c.id, { shouldValidate: false });
            setValue("email", c.email, { shouldValidate: true });
            setValue("name", c.name, { shouldValidate: true });
            setValue("jobTitle", displayJobTitle(c.jobTitle), { shouldValidate: true });
            setValue("phone", c.phone, { shouldValidate: false });
            setShowSuggestions(false);
            setSuggestions([]);
        },
        [setValue],
    );

    /** Any edit to email or name while locked = forking a new contact. */
    const unlock = useCallback(() => {
        if (getValues("contactId")) setValue("contactId", "", { shouldValidate: false });
    }, [getValues, setValue]);

    const onKeyDownEmail = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (!showSuggestions || suggestions.length === 0) return;
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setActiveIndex((i) => (i + 1) % suggestions.length);
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActiveIndex((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
        } else if (e.key === "Enter" && activeIndex >= 0) {
            e.preventDefault();
            selectSuggestion(suggestions[activeIndex]);
        } else if (e.key === "Escape") {
            setShowSuggestions(false);
        }
    };

    // The native <form> submit never reaches React here (the modal is
    // opened from inside a SearchSelect popover — a nested aria dialog —
    // and the submit event gets swallowed), so the Save button drives
    // the submit imperatively. The ref gates re-entry in case both the
    // press and a native submit ever fire for one gesture.
    const submitGate = useRef(false);
    const runSubmit = handleSubmit(async (data) => {
        if (submitGate.current) return;
        submitGate.current = true;
        setSubmitting(true);
        setServerError(null);
        try {
            if (data.contactId) {
                // Existing pool contact — associate, never duplicate.
                await onDone({
                    id: data.contactId,
                    email: data.email.trim(),
                    name: data.name.trim(),
                    jobTitle: jobTitleToWire(data.jobTitle),
                    phone: data.phone.trim(),
                });
                return;
            }
            // The user may have typed a pool email in full without picking
            // the suggestion. Email is the dedup key, so resolve it first
            // and associate the existing record instead of creating a
            // duplicate (design: contact creation & deduplication).
            // Take the endpoint's default page size rather than capping at 5:
            // the search is a substring match, so a small window can push the
            // exact address out of range and we'd create a duplicate.
            const email = data.email.trim();
            const match = (await searchContactPool(email)).find((c) => c.email.toLowerCase() === email.toLowerCase());
            if (match) {
                await onDone(match);
                return;
            }
            const created = await createPoolContact({
                email: data.email.trim(),
                name: data.name.trim().replace(/\s+/g, " "),
                job_title: jobTitleToWire(data.jobTitle),
                phone: data.phone.trim() || null,
            });
            await onDone(created);
        } catch (error) {
            setServerError(error instanceof Error ? error.message : "Failed to save contact");
        } finally {
            submitGate.current = false;
            setSubmitting(false);
        }
    });
    const onSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        void runSubmit();
    };

    return (
        <form onSubmit={onSubmit} className="bg-primary ring-primary flex w-[440px] flex-col rounded-xl ring-1">
            <header className="flex items-start justify-between gap-4 px-6 pt-6 pb-4">
                <h3 className="text-primary text-lg font-semibold">Add Gemini Onboarding Contact</h3>
                <ButtonUtility
                    color="tertiary"
                    size="sm"
                    icon={X}
                    onClick={close}
                    tooltip="Close"
                    data-autotest="uw-information-gemini-onboarding-contacts-add-new-close-button"
                />
            </header>

            <div className="flex flex-col gap-4 px-6">
                <Controller
                    control={control}
                    name="email"
                    render={({ field, fieldState }) => (
                        <div className="relative">
                            <Input
                                size="md"
                                label="Add or Select Email"
                                placeholder="Add Email"
                                data-autotest="uw-information-gemini-onboarding-contacts-add-new-email-input"
                                icon={Mail01}
                                isRequired
                                isInvalid={fieldState.invalid}
                                hint={fieldState.error?.message}
                                value={field.value}
                                onChange={(value) => {
                                    unlock();
                                    field.onChange(value);
                                    setShowSuggestions(true);
                                }}
                                onFocus={() => setShowSuggestions(true)}
                                // Delay the hide so a suggestion click still
                                // fires before the popover unmounts.
                                onBlur={() => {
                                    setTimeout(() => setShowSuggestions(false), 150);
                                    field.onBlur();
                                }}
                                onKeyDown={onKeyDownEmail}
                            />
                            {showSuggestions && !isLocked && suggestions.length > 0 && (
                                <ul
                                    role="listbox"
                                    className="bg-primary ring-secondary_alt absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-lg py-1 shadow-lg ring-1 outline-none"
                                >
                                    {suggestions.map((c, idx) => (
                                        <li key={c.id} role="option" aria-selected={idx === activeIndex}>
                                            <button
                                                type="button"
                                                onMouseDown={(e) => e.preventDefault()}
                                                onClick={() => selectSuggestion(c)}
                                                data-autotest="uw-information-gemini-onboarding-contacts-add-new-email-suggestion"
                                                className={`block w-full cursor-pointer px-3 py-2 text-left transition duration-100 ease-linear ${
                                                    idx === activeIndex ? "bg-primary_hover" : "hover:bg-primary_hover"
                                                }`}
                                            >
                                                {/* Email + name together so two people at the
                                                  * same org are distinguishable; long names
                                                  * wrap to a second line (design note). */}
                                                <div className="text-primary text-sm font-medium break-words">{c.email}</div>
                                                <div className="text-tertiary text-xs break-words">{c.name || "—"}</div>
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                            {isLocked && (
                                <p className="text-tertiary mt-1 inline-flex items-center gap-1 text-xs">
                                    <Check aria-hidden="true" className="text-fg-success-primary size-3.5" />
                                    Using existing contact — edit the name to create a new one
                                </p>
                            )}
                        </div>
                    )}
                />

                <Controller
                    control={control}
                    name="name"
                    render={({ field, fieldState }) => (
                        <Input
                            size="md"
                            label="Name"
                            placeholder="Add Name"
                            data-autotest="uw-information-gemini-onboarding-contacts-add-new-name-input"
                            isRequired
                            isInvalid={fieldState.invalid}
                            hint={fieldState.error?.message}
                            value={field.value}
                            onChange={(value) => {
                                unlock();
                                field.onChange(value);
                            }}
                            onBlur={field.onBlur}
                        />
                    )}
                />

                <Controller
                    control={control}
                    name="jobTitle"
                    render={({ field, fieldState }) => (
                        <div className="flex flex-col gap-1.5">
                            <span className="text-secondary text-sm font-medium">
                                Add or Select Job Title <span className="text-brand-tertiary">*</span>
                            </span>
                            <SelectWithCustom
                                aria-label="Add or Select Job Title"
                                data-autotest="uw-information-gemini-onboarding-contacts-add-new-job-title-select"
                                dataAutotestPrefix="uw-information-gemini-onboarding-contacts-add-new-job-title"
                                placeholder="Select Job Title"
                                value={field.value}
                                onChange={(next) => {
                                    unlock();
                                    field.onChange(next);
                                }}
                                isInvalid={fieldState.invalid}
                                presets={JOB_TITLE_PRESETS}
                            />
                            {fieldState.error?.message && (
                                <p className="text-error-primary text-sm">{fieldState.error.message}</p>
                            )}
                        </div>
                    )}
                />

                <Controller
                    control={control}
                    name="phone"
                    render={({ field }) => (
                        <Input
                            size="md"
                            type="tel"
                            inputMode="tel"
                            label="Phone"
                            data-autotest="uw-information-gemini-onboarding-contacts-add-new-phone-input"
                            placeholder="(555) 000-0000"
                            icon={Phone}
                            value={field.value}
                            // Deliberately does NOT call unlock() like the other
                            // fields: email is the dedup key, so clearing
                            // `contactId` just routes the save through the
                            // exact-email lookup below, which re-associates the
                            // very same pool record. The typed phone is dropped
                            // either way, and unlocking would additionally hide
                            // the "Using existing contact" hint, implying a fork
                            // that never happens. Making a phone edit stick needs
                            // a product decision, not a fork.
                            onChange={(value) => field.onChange(sanitizePhone(value))}
                            onBlur={field.onBlur}
                        />
                    )}
                />

                {serverError && <p className="text-error-primary text-sm">{serverError}</p>}
            </div>

            <div className="flex gap-3 px-6 py-6">
                <Button
                    color="secondary"
                    size="md"
                    className="flex-1"
                    onClick={close}
                    isDisabled={submitting}
                    data-autotest="uw-information-gemini-onboarding-contacts-add-new-cancel-button"
                >
                    Cancel
                </Button>
                <Button
                    type="submit"
                    color="primary"
                    size="md"
                    className="flex-1"
                    isLoading={submitting}
                    showTextWhileLoading
                    onClick={() => void runSubmit()}
                    data-autotest="uw-information-gemini-onboarding-contacts-add-new-save-button"
                >
                    Save
                </Button>
            </div>
        </form>
    );
}
